import { Box, Button } from '@mui/material';
import {
  UndoIcon,
  RedoIcon,
  ContentCopyIcon,
  ContentPasteIcon,
} from '@/shared/ui/display/EmojiIcon';
import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { useGridTheme } from '@/shared/ui/data/EditableGrid/theme';

interface IProps {
  enableUndo?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;

  enableClipboard?: boolean;
  enablePaste?: boolean;
  selectedCells: ReadonlyArray<Position>;
  focusedCell: Position | null;
  isCopying?: boolean;
  isPasting?: boolean;
  onCopy?: () => void;
  onPaste?: () => void;
}

export function GridToolbar({
  enableUndo = false,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  enableClipboard = false,
  enablePaste = true,
  selectedCells,
  focusedCell,
  isCopying = false,
  isPasting = false,
  onCopy,
  onPaste,
}: IProps) {
  const { toolbarSx } = useGridTheme();

  if (!enableUndo && !enableClipboard) return null;

  return (
    <Box className="grid-toolbar" sx={toolbarSx}>
      {enableUndo && (
        <HistoryButtonGroup onUndo={onUndo} canUndo={canUndo} onRedo={onRedo} canRedo={canRedo} />
      )}
      {enableUndo && enableClipboard && (
        <Box sx={{ borderLeft: 1, borderColor: 'divider', mx: 0.5 }} />
      )}
      {enableClipboard && (
        <ClipboardButtonGroup
          onCopy={onCopy}
          onPaste={onPaste}
          enablePaste={enablePaste}
          isCopying={isCopying}
          isPasting={isPasting}
          selectedCells={selectedCells}
          focusedCell={focusedCell}
        />
      )}
    </Box>
  );
}

function HistoryButtonGroup({
  onUndo,
  canUndo,
  onRedo,
  canRedo,
}: Pick<IProps, 'onUndo' | 'canUndo' | 'onRedo' | 'canRedo'>) {
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={onUndo}
        disabled={!canUndo}
        startIcon={<UndoIcon />}
      >
        Undo
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={onRedo}
        disabled={!canRedo}
        startIcon={<RedoIcon />}
      >
        Redo
      </Button>
    </>
  );
}

function ClipboardButtonGroup({
  onCopy,
  onPaste,
  enablePaste = true,
  isCopying,
  isPasting,
  selectedCells,
  focusedCell,
}: Pick<IProps, any>) {
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={onCopy}
        disabled={selectedCells.length === 0 || isCopying}
        startIcon={<ContentCopyIcon />}
      >
        Copy
      </Button>
      {enablePaste && (
        <Button
          variant="outlined"
          size="small"
          onClick={onPaste}
          disabled={focusedCell === null || isPasting}
          startIcon={<ContentPasteIcon />}
        >
          Paste
        </Button>
      )}
    </>
  );
}
